import { Arl, ArlDTO } from "@/interfaces/Arl";
import axiosBaseQuery from "@/utils/axiosBaseQuery";
import { createApi } from "@reduxjs/toolkit/query/react";

const isValidArl = (arl: Arl | null | undefined): arl is Arl =>
  arl != null && typeof arl.id === "number";

const normalizeArl = (response: Arl | null | undefined): Arl | null =>
  isValidArl(response) ? response : null;

/**
 * IMPORTANTE:
 * Debido a una limitación actual, no se pueden exportar los hooks individualmente sin causar problemas de tipos.
 * El siguiente código es un intento de exportar el hook useLoginMutation, pero resulta en el siguiente error de TypeScript:
 * "El tipo inferido de 'useLoginMutation' no se puede nombrar sin una referencia a '../../../node_modules/@reduxjs/toolkit/dist/query/react/buildHooks'. Es probable que esto no sea portátil. Es necesaria una anotación de tipo.ts(2742)"
 * en su lugar llamar directamente a authService y acceder al hook
 * @example
   arlService.findAll(...params)
 */
export const arlService = createApi({
  reducerPath: "arlApi",
  baseQuery: axiosBaseQuery,
  tagTypes: ["Arls"],
  endpoints: (builder) => ({
    findAll: builder.query<Arl[], void>({
      query: () => ({
        url: "/arl/",
        method: "GET",
      }),
      transformResponse: (response: Arl[] | null) =>
        Array.isArray(response) ? response.filter(isValidArl) : [],
      providesTags: ["Arls"],
    }),
    findById: builder.query<Arl, { id: number }>({
      query: ({ id }) => ({
        url: `/arl/${id}`,
        method: "GET",
      }),
      transformResponse: (response: Arl) => {
        const arl = normalizeArl(response);
        if (!arl) {
          throw new Error("ARL no encontrada");
        }
        return arl;
      },
    }),
    updateArl: builder.mutation<Arl, Partial<Arl>>({
      query: (updatedArl) => ({
        url: `/arl/update`,
        method: "PATCH",
        data: {
          ...updatedArl,
        },
      }),
      transformResponse: (response: Arl) => {
        const arl = normalizeArl(response);
        if (!arl) {
          throw new Error("Respuesta inválida al actualizar ARL");
        }
        return arl;
      },
      invalidatesTags: ["Arls"],
    }),
    deleteArl: builder.mutation<Arl, { id: number }>({
      query: ({ id }) => ({
        url: `/arl/delete/${id}`,
        method: "DELETE",
      }),
      transformResponse: (response: Arl) => {
        const arl = normalizeArl(response);
        if (!arl) {
          throw new Error("Respuesta inválida al eliminar ARL");
        }
        return arl;
      },
      invalidatesTags: ["Arls"],
    }),
    save: builder.mutation<Arl, ArlDTO>({
      query: (arlData) => ({
        url: `/arl/save`,
        method: "POST",
        data: {
          ...arlData,
        },
      }),
      transformResponse: (response: Arl) => {
        const arl = normalizeArl(response);
        if (!arl) {
          throw new Error("Respuesta inválida al crear ARL");
        }
        return arl;
      },
      invalidatesTags: ["Arls"],
    }),
  }),
});
